import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { useTheme } from '../contexts/ThemeContext';
import { Product, Category, Order, OrderItem } from '../types';
import { productsAPI, ordersAPI, tablesAPI, settingsAPI, giftCodesAPI, loyaltyCodesAPI, realtimeCartAPI } from '../services/api';
import toast from 'react-hot-toast';
import { Button, Badge, Card, ProductCard, Modal } from './ui';
import { printStandardReceipt } from '../utils/printReceipt';

const TableOrder: React.FC = () => {
  const { tableId, orderId } = useParams<{ tableId: string; orderId?: string }>();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { formatPrice } = useCurrency();
  const { theme, toggleTheme, isDark } = useTheme();

  const [products, setProducts] = useState<Product[]>([]);
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [cart, setCart] = useState<OrderItem[]>([]);
  const [order, setOrder] = useState<Order | null>(null);
  const [table, setTable] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [productNote, setProductNote] = useState('');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentType, setPaymentType] = useState<'nakit' | 'kart' | 'qr'>('nakit');
  const [completedOrder, setCompletedOrder] = useState<any>(null);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [wantsReceipt, setWantsReceipt] = useState(false);
  
  // Hediye kodu state'leri
  const [giftCode, setGiftCode] = useState('');
  const [appliedGiftCode, setAppliedGiftCode] = useState<any>(null);
  const [giftCodeDiscount, setGiftCodeDiscount] = useState(0);
  const [isValidatingCode, setIsValidatingCode] = useState(false);
  
  // Sadakat kodu state'leri
  const [loyaltyCode, setLoyaltyCode] = useState('');
  const [appliedLoyaltyCode, setAppliedLoyaltyCode] = useState<any>(null);
  const [tempLoyaltyCount, setTempLoyaltyCount] = useState(0);
  const [isValidatingLoyaltyCode, setIsValidatingLoyaltyCode] = useState(false);
  const [pendingLoyaltyReward, setPendingLoyaltyReward] = useState<any>(null);
  const [receiptSettings, setReceiptSettings] = useState({
    receiptTitle: '',
    receiptSubtitle: '',
    receiptPhone: '',
    receiptTaxNumber: '',
    receiptFooter: '',
    receiptDateLabel: '',
    receiptTimeLabel: '',
    receiptOrderNumberLabel: '',
    receiptOrderDetailsLabel: '',
    receiptPaymentLabel: '',
    receiptPaymentCash: '',
    receiptPaymentCard: '',
    receiptSubtotalLabel: '',
    receiptTaxLabel: '',
    receiptTotalLabel: '',
    receiptQuantityLabel: '',
    receiptShowTaxDetails: false,
    receiptShowPaymentType: false,
    receiptShowOrderNumber: false,
    receiptShowDateTime: false
  });

  // Fiş ayarlarını yükle
  const loadReceiptSettings = async () => {
    try {
      const settings = await settingsAPI.getReceiptSettings();
      setReceiptSettings({
        receiptTitle: settings.receiptTitle || '',
        receiptSubtitle: settings.receiptSubtitle || '',
        receiptPhone: settings.receiptPhone || '',
        receiptTaxNumber: settings.receiptTaxNumber || '',
        receiptFooter: settings.receiptFooter || '',
        receiptDateLabel: settings.receiptDateLabel || '',
        receiptTimeLabel: settings.receiptTimeLabel || '',
        receiptOrderNumberLabel: settings.receiptOrderNumberLabel || '',
        receiptOrderDetailsLabel: settings.receiptOrderDetailsLabel || '',
        receiptPaymentLabel: settings.receiptPaymentLabel || '',
        receiptPaymentCash: settings.receiptPaymentCash || '',
        receiptPaymentCard: settings.receiptPaymentCard || '',
        receiptSubtotalLabel: settings.receiptSubtotalLabel || '',
        receiptTaxLabel: settings.receiptTaxLabel || '',
        receiptTotalLabel: settings.receiptTotalLabel || '',
        receiptQuantityLabel: settings.receiptQuantityLabel || '',
        receiptShowTaxDetails: settings.receiptShowTaxDetails || false,
        receiptShowPaymentType: settings.receiptShowPaymentType || false,
        receiptShowOrderNumber: settings.receiptShowOrderNumber || false,
        receiptShowDateTime: settings.receiptShowDateTime || false
      });
    } catch (error) {
      // console.error('Fiş ayarları yüklenemedi:', error);
    }
  };

  useEffect(() => {
    loadData();
    loadReceiptSettings(); // Fiş ayarlarını yükle
  }, [tableId, orderId]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Masa bilgilerini yükle
      const tableData = await tablesAPI.getById(parseInt(tableId!));
      setTable(tableData);

      // Ürünler ve kategorileri yükle
      const [productsData, categoriesData] = await Promise.all([
        productsAPI.getAll(),
        productsAPI.getCategories()
      ]);
      const activeProducts = productsData.filter((p: Product) => p.is_active);
      setAllProducts(activeProducts);
      setProducts(activeProducts);
      setCategories(categoriesData);

             // Eğer orderId varsa, mevcut siparişi yükle
       if (orderId) {
         const orderData = await ordersAPI.getById(parseInt(orderId));
         setOrder(orderData);
         // Eğer sipariş boşsa (total_amount = 0), sepeti boş yap
         if (orderData.total_amount === 0) {
           setCart([]);
         } else {
           setCart(orderData.items || []);
         }
       } else {
         // Yeni sipariş
         setOrder(null);
         setCart([]);
       }
    } catch (error) {
      // console.error('Veri yüklenirken hata:', error);
      toast.error('Veri yüklenirken hata oluştu!');
    } finally {
      setLoading(false);
    }
  };

  const addToCart = (product: Product, note?: string) => {
    const existingItemIndex = cart.findIndex(
      item => item.product_id === product.id && (item.notes || '') === (note || '')
    );

    if (existingItemIndex !== -1) {
      const newCart = [...cart];
      newCart[existingItemIndex].quantity += 1;
      setCart(newCart);
    } else {
      const newItem: OrderItem = {
        id: Date.now(),
        order_id: order?.id || 0,
        product_id: product.id,
        quantity: 1,
        unit_price: product.price,
        notes: note || '',
        created_at: new Date().toISOString(),
        product: product
      };
      setCart([...cart, newItem]);
    }
    toast.success(`${product.name} sepete eklendi`);
  };

  const openNoteModal = (product: Product) => {
    setSelectedProduct(product);
    setProductNote('');
    setShowNoteModal(true);
  };

  const addWithNote = () => {
    if (selectedProduct) {
      addToCart(selectedProduct, productNote);
      setShowNoteModal(false);
      setSelectedProduct(null);
      setProductNote('');
    }
  };

  const updateQuantity = (index: number, newQuantity: number) => {
    if (newQuantity <= 0) {
      removeFromCart(index);
      return;
    }
    const newCart = [...cart];
    newCart[index].quantity = newQuantity;
    setCart(newCart);
  };

  const removeFromCart = (index: number) => {
    const newCart = cart.filter((_, i) => i !== index);
    setCart(newCart);
  };

  // Arama ve kategori filtresi için ürün filtreleme
  useEffect(() => {
    let filtered = allProducts;
    
    if (selectedCategory !== null) {
      filtered = filtered.filter(p => p.category_id === selectedCategory);
    }
    
    if (searchTerm) {
      filtered = filtered.filter(p => 
        p.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    setProducts(filtered);
  }, [selectedCategory, searchTerm, allProducts]);

  const clearCart = async () => {
    // Eğer mevcut bir sipariş varsa, siparişi iptal et
    if (order) {
      try {
        // Siparişi iptal et
        await ordersAPI.cancel(order.id);
        
        // Order ve cart'ı temizle
        setOrder(null);
        setCart([]);
        
        toast.success('✅ Sipariş iptal edildi ve masa temizlendi');
        
        // Masa durumunu yeniden yükle
        if (tableId) {
          setTimeout(async () => {
            try {
              const updatedTable = await tablesAPI.getById(parseInt(tableId));
              setTable(updatedTable);
            } catch (error) {
              // console.error('Masa durumu güncellenirken hata:', error);
            }
          }, 500);
        }
      } catch (error) {
        // console.error('Sipariş iptal edilirken hata:', error);
        toast.error('Sipariş iptal edilemedi!');
      }
    } else {
      // Sadece sepeti temizle
      setCart([]);
      toast.success('✅ Sepet temizlendi');
    }
  };

  const getTotalAmount = () => {
    return cart.reduce((sum, item) => {
      // Ödül ürünlerini ücretsiz yap
      if (item.notes === '🎁 ÖDÜL') return sum;
      return sum + (item.quantity * item.unit_price);
    }, 0);
  };

  // Vergi hesaplama (Fiyata Dahil Mod - Belçika)
  const getTaxAmount = () => {
    return cart.reduce((sum, item) => {
      // Ödül ürünlerini hariç tut
      if (item.notes === '🎁 ÖDÜL') return sum;
      const itemTotal = item.quantity * item.unit_price;
      const taxRate = item.product?.tax_rate || 0;
      const taxAmount = itemTotal - (itemTotal / (1 + taxRate / 100));
      return sum + taxAmount;
    }, 0);
  };

  // Vergi oranı bilgisini al (sepet için)
  const getTaxRateInfo = () => {
    const taxRates = new Set<number>();
    cart.forEach(item => {
      if (item.notes !== '🎁 ÖDÜL' && item.product?.tax_rate) {
        taxRates.add(item.product.tax_rate);
      }
    });

    if (taxRates.size === 0) return 'KDV yok';
    if (taxRates.size === 1) return `KDV (%${Array.from(taxRates)[0]})`;
    return 'KDV (Farklı oranlar)';
  };

  const getGrandTotal = () => {
    return Math.max(0, getTotalAmount() - giftCodeDiscount); // Fiyatlar zaten vergili, hediye kodu indirimi uygula
  };

  const saveOrder = async () => {
    if (cart.length === 0) {
      toast.error('Sepet boş! Lütfen ürün ekleyin.');
      return;
    }

    try {
      const orderData = {
        table_id: parseInt(tableId!),
        user_id: user!.id,
        items: cart.map(item => ({
          product_id: item.product_id,
          quantity: item.quantity,
          unit_price: item.unit_price,
          notes: item.notes || ''
        }))
      };

      if (order) {
        // Mevcut siparişi güncelle - sadece yeni eklenen items'ları gönder
        const existingItemsCount = order.items?.length || 0;
        const newItems = cart.slice(existingItemsCount); // Sadece yeni items'ları al
        
        const updateData = {
          items: newItems.map(item => ({
            product_id: item.product_id,
            quantity: item.quantity,
            unit_price: item.unit_price,
            notes: item.notes || ''
          }))
        } as any; // Backend items'ı partial kabul ediyor
        
        const updateResult = await ordersAPI.update(order.id, updateData);
        
        // Toplam tutarı hesapla
        const totalAmount = cart.reduce((sum, item) => {
          if (item.notes === '🎁 ÖDÜL') return sum;
          return sum + ((item.product?.price || 0) * item.quantity);
        }, 0);
        
        toast.success('✅ Sipariş güncellendi ve mutfağa gönderildi!');
        
        // Order'ı güncelle
        const updatedOrder: Order = {
          ...order,
          total_amount: totalAmount,
          status: 'mutfak', // Güncellenen sipariş mutfağa geri gider
          items: cart
        };
        setOrder(updatedOrder);
      } else {
        // Yeni sipariş oluştur
        const newOrder = await ordersAPI.create(orderData);
        
        // Toplam tutarı hesapla (ödül ürünlerini hariç tut)
        const totalAmount = cart.reduce((sum, item) => {
          if (item.notes === '🎁 ÖDÜL') return sum;
          return sum + ((item.product?.price || 0) * item.quantity);
        }, 0);
        
        toast.success('✅ Sipariş kaydedildi ve mutfağa gönderildi!');
        
        // Full order object oluştur
        const fullOrder: Order = {
          ...newOrder,
          table_id: parseInt(tableId!),
          user_id: user!.id,
          total_amount: totalAmount,
          status: 'mutfak',
          created_at: new Date().toISOString(),
          items: cart
        };
        setOrder(fullOrder);
      }
    } catch (error) {
      // console.error('Sipariş kaydedilirken hata:', error);
      toast.error('Sipariş kaydedilemedi!');
    }
  };

  const processPayment = async () => {
    if (!order) {
      toast.error('Sipariş bulunamadı!');
      return;
    }

    try {
      setProcessing(true);
      
      // Ödemeyi al - hediye kodu bilgilerini backend'e gönder
      await ordersAPI.pay(
        order.id, 
        paymentType,
        appliedGiftCode?.id,
        giftCodeDiscount
      );
      
      if (appliedGiftCode && giftCodeDiscount > 0) {
        toast.success(`🎁 Hediye kodu kullanıldı: -${formatPrice(giftCodeDiscount)}`);
      }
      
      // Bekleyen ödül kullanımını kaydet
      if (pendingLoyaltyReward) {
        try {
          await loyaltyCodesAPI.useReward(
            pendingLoyaltyReward.loyaltyCodeId,
            pendingLoyaltyReward.productId,
            pendingLoyaltyReward.tempLoyaltyCount
          );
          toast.success(`🎁 Ödül kullanıldı!`);
        } catch (error) {
          toast.error('Ödül kullanımı kaydedilemedi, ancak ödeme tamamlandı');
        }
      }

      toast.success('✅ Ödeme başarıyla alındı!');
      setShowPaymentModal(false);
      
      // Ödeme başarılı oldu, state'leri temizle
      setGiftCode('');
      setAppliedGiftCode(null);
      setGiftCodeDiscount(0);
      setLoyaltyCode('');
      setAppliedLoyaltyCode(null);
      setTempLoyaltyCount(0);
      setPendingLoyaltyReward(null);
      
      // Müşteri ekranını temizle (ödemeden sonra)
      try {
        await realtimeCartAPI.clearAfterPayment();
      } catch (e) {}

      // Mevcut order'ı kullan ve fiş modalını göster
      setCompletedOrder(order);
      setShowReceiptModal(true);
    } catch (error: any) {
      // console.error('Ödeme işlenirken hata:', error);
      
      // Daha detaylı hata mesajı
      if (error.response?.data?.error) {
        toast.error(`Ödeme hatası: ${error.response.data.error}`);
      } else if (error.message) {
        toast.error(`Ödeme hatası: ${error.message}`);
      } else {
        toast.error('Ödeme işlenirken hata oluştu!');
      }
    } finally {
      setProcessing(false);
    }
  };

  // Ödeme modalı açıldığında müşteri ekranında masa ve ürünleri göster
  const showCustomerPreviewForPayment = async () => {
    try {
      const subtotal = cart.reduce((sum, item) => item.notes === '🎁 ÖDÜL' ? sum : sum + (item.unit_price * item.quantity), 0);
      const taxTotal = cart.reduce((sum, item) => {
        if (item.notes === '🎁 ÖDÜL') return sum;
        const itemTotal = item.unit_price * item.quantity;
        const taxRate = item.product?.tax_rate || 0;
        const taxAmount = itemTotal - (itemTotal / (1 + taxRate / 100));
        return sum + taxAmount;
      }, 0);
      const totals = {
        subtotal,
        tax: taxTotal,
        total: Math.max(0, subtotal - giftCodeDiscount),
        discount: giftCodeDiscount
      };
      const tableNum = table?.table_number ?? null;
      const computedTableLabel = tableNum !== null && tableNum !== undefined
        ? (tableNum <= 18 ? `Masa T${tableNum}` : `Masa C${tableNum - 18}`)
        : (tableId ? `Masa T${parseInt(tableId)}` : undefined);

      const payload = {
        items: cart.map(ci => ({
          id: ci.product_id,
          product_id: ci.product_id,
          product_name: ci.product?.name || '',
          quantity: ci.quantity,
          unit_price: ci.unit_price,
          notes: ci.notes || '',
          is_reward: ci.notes === '🎁 ÖDÜL'
        })),
        totals,
        enabled: true,
        coupons: {
          giftCode: appliedGiftCode ? {
            code: appliedGiftCode.code,
            discount: (appliedGiftCode.discount_amount ?? appliedGiftCode.discount ?? giftCodeDiscount ?? 0),
            type: appliedGiftCode.type
          } : null,
          loyaltyCode: appliedLoyaltyCode ? {
            code: appliedLoyaltyCode.code,
            currentCount: appliedLoyaltyCode.current_count + (tempLoyaltyCount || 0),
            requiredQuantity: appliedLoyaltyCode.required_quantity,
            isCompleted: appliedLoyaltyCode.is_completed || (appliedLoyaltyCode.current_count + (tempLoyaltyCount || 0)) >= appliedLoyaltyCode.required_quantity,
            rewardUsedAt: appliedLoyaltyCode.reward_used_at
          } : null
        },
        // Ek bilgi: müşteri ekranı için masa bilgisi ve önizleme bayrağı
        tableId: table?.id ?? parseInt(tableId || '0'),
        tableNumber: tableNum ?? null,
        tableLabel: computedTableLabel,
        previewForPayment: true
      } as any;
      await realtimeCartAPI.update(payload);
    } catch (e) {
      // Müşteri ekranına gönderilemediğinde sessiz geç
    }
  };

  // Ödeme modalı kapandığında müşteri ekranı önizlemesini temizle
  const clearCustomerPreviewForPayment = async () => {
    try {
      const payload = {
        items: [],
        totals: { subtotal: 0, tax: 0, total: 0, discount: 0 },
        enabled: false,
        coupons: { giftCode: null, loyaltyCode: null }
      } as any;
      await realtimeCartAPI.update(payload);
    } catch (e) {}
    try { await realtimeCartAPI.clearAfterPayment(); } catch (e) {}
  };

  // Hediye kodu doğrulama
  const validateGiftCode = async () => {
    if (!giftCode.trim()) {
      toast.error('Lütfen bir hediye kodu girin');
      return;
    }

    setIsValidatingCode(true);
    try {
      const cartTotal = getGrandTotal();
      const cartItems = cart.map(item => ({
        product_id: item.product_id,
        quantity: item.quantity,
        unit_price: item.unit_price
      }));

      const validation = await giftCodesAPI.validate(giftCode.trim(), cartTotal, cartItems);
      
      if (validation.valid && validation.giftCode) {
        // İndirim miktarını hesapla
        let discountAmount = 0;
        if (validation.giftCode.discount_type === 'percentage') {
          discountAmount = (cartTotal * validation.giftCode.discount_value) / 100;
        } else {
          discountAmount = validation.giftCode.discount_value;
        }
        
        setAppliedGiftCode(validation.giftCode);
        setGiftCodeDiscount(discountAmount);
        toast.success(`✅ ${validation.message}`);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Kod doğrulanamadı');
      setAppliedGiftCode(null);
      setGiftCodeDiscount(0);
    } finally {
      setIsValidatingCode(false);
    }
  };

  // Hediye kodu kaldırma
  const removeGiftCode = () => {
    setGiftCode('');
    setAppliedGiftCode(null);
    setGiftCodeDiscount(0);
    toast.success('Hediye kodu kaldırıldı');
  };

  // Sadakat kodu doğrulama
  const validateLoyaltyCode = async () => {
    if (!loyaltyCode.trim()) {
      toast.error('Lütfen bir sadakat kodu girin');
      return;
    }

    setIsValidatingLoyaltyCode(true);
    try {
      const foundCode = await loyaltyCodesAPI.validate(loyaltyCode.trim());
      
      if (foundCode.reward_used_at) {
        toast.error('❌ Bu ödül zaten kullanılmış');
        setAppliedLoyaltyCode(null);
        return;
      }

      if (foundCode.is_completed && !foundCode.reward_used_at) {
        setAppliedLoyaltyCode(foundCode);
        toast.success(`🎉 Sadakat ödülü kullanılabilir! ${foundCode.reward_product_name}`);
      } else if (foundCode.is_completed && foundCode.reward_used_at) {
        toast.error('❌ Bu ödül zaten kullanılmış');
        setAppliedLoyaltyCode(null);
      } else {
        const targetProductQuantity = cart.reduce((total, item) => {
          return item.product_id === foundCode.target_product_id 
            ? total + item.quantity 
            : total;
        }, 0);
        
        const tempCount = targetProductQuantity > 0 ? targetProductQuantity : 1;
        const newTotalCount = foundCode.current_count + tempCount;
        
        setAppliedLoyaltyCode(foundCode);
        setTempLoyaltyCount(tempCount);
        
        if (newTotalCount >= foundCode.required_quantity) {
          toast.success(`🎉 Sadakat kodu tamamlanacak: ${newTotalCount}/${foundCode.required_quantity}`);
        } else {
          toast.success(`📊 Sadakat kodu eklendi: ${newTotalCount}/${foundCode.required_quantity}`);
        }
      }
    } catch (error: any) {
      toast.error(`Kod doğrulanamadı: ${error.message || 'Bilinmeyen hata'}`);
      setAppliedLoyaltyCode(null);
    } finally {
      setIsValidatingLoyaltyCode(false);
    }
  };

  // Sadakat kodunu kaldır
  const removeLoyaltyCode = () => {
    setCart(prevCart => prevCart.filter(item => item.notes !== '🎁 ÖDÜL'));
    setAppliedLoyaltyCode(null);
    setTempLoyaltyCount(0);
    setLoyaltyCode('');
    setPendingLoyaltyReward(null);
    toast.success('✅ Sadakat kodu kaldırıldı');
  };

  // Sadakat ödülünü kullan
  const useLoyaltyReward = () => {
    const isCompleted = appliedLoyaltyCode.is_completed || 
      (tempLoyaltyCount > 0 && (appliedLoyaltyCode.current_count + tempLoyaltyCount) >= appliedLoyaltyCode.required_quantity);
    
    if (!appliedLoyaltyCode || !isCompleted || appliedLoyaltyCode.reward_used_at || pendingLoyaltyReward) {
      toast.error('Kullanılabilir ödül bulunamadı veya ödül zaten kullanılmış/beklemede');
      return;
    }

    try {
      const rewardProduct = allProducts.find(p => p.id === appliedLoyaltyCode.reward_product_id);
      
      if (!rewardProduct) {
        toast.error('❌ Ödül ürünü bulunamadı!');
        return;
      }

      const newItem: OrderItem = {
        id: Date.now(),
        order_id: order?.id || 0,
        product_id: rewardProduct.id,
        quantity: 1,
        unit_price: rewardProduct.price,
        notes: '🎁 ÖDÜL',
        created_at: new Date().toISOString(),
        product: rewardProduct
      };
      
      setCart(prevCart => [...prevCart, newItem]);
      
      toast.success(`🎉 ${rewardProduct.name} ücretsiz eklendi!`);
      
      setPendingLoyaltyReward({
        loyaltyCodeId: appliedLoyaltyCode.id,
        productId: rewardProduct.id,
        tempLoyaltyCount: tempLoyaltyCount
      });
    } catch (error: any) {
      toast.error('❌ Ödül sepete eklenemedi');
    }
  };

  const printReceipt = () => {
    const orderToPrint = completedOrder || order;
    if (orderToPrint) {
      printStandardReceipt(orderToPrint, receiptSettings, formatPrice);
    }
  };

  const finishOrder = () => {
    setShowReceiptModal(false);
    setCompletedOrder(null);
    // Masalar sayfasına geri dön
    navigate('/tables');
  };

  const filteredProducts = selectedCategory === 0 
    ? products 
    : products.filter(product => product.category_id === selectedCategory);

  if (loading) {
    return (
      <div className="min-h-screen bg-cafe-cream dark:bg-dark-bg flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cafe-brown mx-auto mb-4"></div>
          <span className="text-cafe-brown dark:text-cafe-light">Yükleniyor...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cafe-cream dark:bg-dark-bg transition-colors duration-500">
      <div className="container mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-cafe-brown dark:text-cafe-light">
              Masa {table?.table_number <= 18 ? `T${table?.table_number}` : `C${table?.table_number - 18}`} - Sipariş {order ? 'Düzenle' : 'Ver'}
            </h1>
            <p className="text-gray-600 dark:text-dark-text mt-1">
              Hoş geldiniz, {user?.username} ({user?.role})
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={toggleTheme}
              className="bg-gray-500 hover:bg-gray-600 text-white px-3 py-1.5 text-sm rounded-md transition-colors"
              title="Tema değiştir"
            >
              {isDark ? '☀️' : '🌙'}
            </button>
            <Button
              variant="secondary"
              size="md"
              onClick={() => navigate('/tables')}
            >
              ⬅️ Masalara Dön
            </Button>
            <Button
              variant="danger"
              size="md"
              onClick={logout}
            >
              🚪 Çıkış
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Sol Panel - Kategoriler */}
          <div className="lg:col-span-2">
            <div className="bg-white dark:bg-dark-surface rounded-xl shadow-lg p-4 border border-gray-100 dark:border-dark-border">
              <h3 className="text-lg font-semibold mb-4 text-gray-700 dark:text-dark-text">
                📁 Kategoriler
              </h3>
              
              {/* Arama */}
              <div className="mb-4">
                <input
                  type="text"
                  placeholder="Ürün Ara..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border rounded-lg focus:ring-2 focus:ring-cafe-brown focus:border-cafe-brown dark:bg-dark-bg dark:text-dark-text text-sm"
                />
              </div>
              
              {/* Kategori Listesi */}
              <div className="space-y-2">
                <Button
                  variant={selectedCategory === null ? 'primary' : 'secondary'}
                  size="sm"
                  fullWidth
                  onClick={() => setSelectedCategory(null)}
                  className="justify-start text-left"
                >
                  📦 Tümü
                </Button>
                {categories.map((category) => (
                  <Button
                    key={category.id}
                    variant={selectedCategory === category.id ? 'primary' : 'secondary'}
                    size="sm"
                    fullWidth
                    onClick={() => setSelectedCategory(category.id)}
                    className="justify-start text-left"
                  >
                    📁 {category.name}
                  </Button>
                ))}
              </div>
            </div>
          </div>

          {/* Orta Panel - Ürünler */}
          <div className="lg:col-span-7">
            <div className="bg-white dark:bg-dark-surface rounded-xl shadow-lg p-6 border border-gray-100 dark:border-dark-border">
              <h3 className="text-lg font-semibold mb-4 text-gray-700 dark:text-dark-text">
                🍽️ Ürünler
              </h3>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 max-h-[600px] overflow-y-auto">
                {products.map((product) => (
                  <div 
                    key={product.id} 
                    className="bg-white dark:bg-dark-surface rounded-lg shadow-sm border-2 border-gray-300 dark:border-gray-600 overflow-hidden cursor-pointer hover:shadow-md hover:border-gray-400 dark:hover:border-gray-500 transition-all duration-150"
                    onClick={() => addToCart(product)}
                    title={`${product.name} - ${formatPrice(product.price)}`}
                  >
                    {/* Ürün Görseli */}
                    <div className="relative h-24 bg-gray-100 dark:bg-gray-700">
                      {product.image_url ? (
                        <img
                          src={product.image_url.startsWith('http') ? product.image_url : `${process.env.REACT_APP_API_URL || window.location.origin}${product.image_url}`}
                          alt={product.name}
                          className="w-full h-full object-cover rounded-t-lg"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            target.parentElement!.innerHTML = '<div class="flex items-center justify-center h-full text-gray-400 dark:text-gray-500"><span class="text-lg">🖼️</span></div>';
                          }}
                        />
                      ) : (
                        <div className="flex items-center justify-center h-full text-gray-400 dark:text-gray-500">
                          <span className="text-2xl">🍽️</span>
                        </div>
                      )}
                      
                      {/* Stok Bilgisi - Sağ Üst Köşe */}
                      {product.stock_quantity !== null && product.stock_quantity !== undefined && (
                        <div className="absolute top-1 right-1">
                          <span className={`px-1.5 py-0.5 rounded text-xs font-medium bg-white/90 backdrop-blur-sm ${
                            product.stock_quantity > 0
                              ? 'text-green-800'
                              : 'text-red-800'
                          }`}>
                            S: {product.stock_quantity > 0 ? product.stock_quantity : 'Yok'}
                          </span>
                        </div>
                      )}
                    </div>
                    
                    <div className="p-2">
                      <h3 className="font-medium text-gray-900 dark:text-dark-text mb-1 text-sm" style={{ 
                        display: '-webkit-box', 
                        WebkitLineClamp: 2, 
                        WebkitBoxOrient: 'vertical', 
                        overflow: 'hidden' 
                      }}>
                        {product.name}
                      </h3>

                      <div className="flex items-center justify-between">
                        <div className="flex flex-col">
                          <span className="text-base font-bold text-cafe-brown dark:text-cafe-light">
                            {formatPrice(product.price)}
                          </span>
                        </div>
                        
                        {/* Not Ekleme Butonu */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation(); // Ana kartın tıklama olayını engelle
                            openNoteModal(product);
                          }}
                          className="bg-blue-500 text-white p-1.5 rounded text-xs hover:bg-blue-600 transition-colors"
                          title="Not ekle"
                        >
                          📝
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {products.length === 0 && (
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">🔍</div>
                  <h3 className="text-lg font-medium text-gray-600 dark:text-gray-400 mb-2">
                    {searchTerm ? 'Arama kriterinize uygun ürün bulunamadı' : 'Bu kategoride ürün bulunmuyor'}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-500">
                    {searchTerm ? 'Farklı arama terimleri deneyin' : 'Başka bir kategori seçin'}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Sağ Panel - Sepet */}
          <div className="lg:col-span-3">
            <div className="bg-white dark:bg-dark-surface rounded-xl shadow-lg p-6 border border-gray-100 dark:border-dark-border sticky top-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-gray-800 dark:text-dark-text">
                Sepet ({cart.length} ürün)
              </h3>
              {cart.length > 0 && (
                <button
                  onClick={clearCart}
                  className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 text-sm font-medium"
                  title="Tema değiştir"
                >
                  🗑️ Boşalt
                </button>
              )}
            </div>
            
            {cart.length === 0 ? (
              <p className="text-gray-500 dark:text-gray-400 text-center py-8">
                Sepet boş
              </p>
            ) : (
              <>
                <div className="space-y-3 mb-6 max-h-96 overflow-y-auto">
                  {cart.map((item, index) => (
                    <div key={index} className="flex justify-between items-center border-b border-gray-200 dark:border-dark-border pb-3">
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-800 dark:text-dark-text">
                          {item.product?.name}
                        </h4>
                        {item.notes && (
                          <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                            📝 {item.notes}
                          </p>
                        )}
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {formatPrice(item.unit_price)} × {item.quantity}
                        </p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => updateQuantity(index, item.quantity - 1)}
                          className="w-8 h-8 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                        >
                          -
                        </button>
                        <span className="w-8 text-center font-medium text-gray-800 dark:text-dark-text">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => updateQuantity(index, item.quantity + 1)}
                          className="w-8 h-8 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                        >
                          +
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="border-t border-gray-200 dark:border-dark-border pt-4">
                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Ara Toplam:</span>
                      <span className="text-gray-700 dark:text-gray-300">
                        {formatPrice(getTotalAmount())}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">{getTaxRateInfo()}:</span>
                      <span className="text-gray-700 dark:text-gray-300">
                        {formatPrice(getTaxAmount())}
                      </span>
                    </div>
                    <div className="flex justify-between items-center border-t border-gray-200 dark:border-dark-border pt-2">
                      <span className="text-lg font-semibold text-gray-800 dark:text-dark-text">Genel Toplam:</span>
                      <span className="text-2xl font-bold text-cafe-brown dark:text-cafe-light">
                        {formatPrice(getGrandTotal())}
                      </span>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <button
                      onClick={saveOrder}
                      className="w-full bg-gradient-to-r from-cafe-brown to-cafe-dark text-white py-3 rounded-lg hover:from-cafe-dark hover:to-cafe-brown transition-all duration-200 font-semibold"
                    >
                      {order ? 'Siparişi Güncelle' : 'Siparişi Kaydet'}
                    </button>
                    
                    {order && (
                      user?.can_process_payments || user?.role === 'admin' ? (
                        <button
                          onClick={async () => { await showCustomerPreviewForPayment(); setShowPaymentModal(true); }}
                          className="w-full bg-gradient-to-r from-green-500 to-green-600 text-white py-3 rounded-lg hover:from-green-600 hover:to-green-700 transition-all duration-200 font-semibold"
                        >
                          💳 Ödeme Al
                        </button>
                      ) : (
                        <button
                          disabled
                          className="w-full bg-gray-400 text-white py-3 rounded-lg cursor-not-allowed font-semibold"
                          title="Tema değiştir"
                        >
                          🚫 Ödeme İzni Yok
                        </button>
                      )
                    )}
                  </div>
                </div>
              </>
            )}
            </div>
          </div>
        </div>
      </div>

      {/* Note Modal */}
      <Modal
        isOpen={showNoteModal}
        onClose={() => setShowNoteModal(false)}
        title={`${selectedProduct?.name} için not ekle`}
        size="sm"
      >
            <textarea
              value={productNote}
              onChange={(e) => setProductNote(e.target.value)}
              placeholder="Ürün notu ekleyin..."
              className="w-full p-3 border border-gray-300 dark:border-dark-border rounded-lg resize-none dark:bg-dark-bg dark:text-dark-text"
              rows={4}
            />
            <div className="flex space-x-3 mt-4">
              <Button
                variant="primary"
                size="md"
                fullWidth
                onClick={addWithNote}
              >
                Sepete Ekle
              </Button>
              <Button
                variant="secondary"
                size="md"
                fullWidth
                onClick={() => setShowNoteModal(false)}
              >
                İptal
              </Button>
            </div>
          </Modal>

      {/* Payment Modal */}
      <Modal
        isOpen={showPaymentModal && !!order}
        onClose={() => {
          setShowPaymentModal(false);
          // Modal kapatılınca state'leri TEMİZLEME - masa siparişi devam ediyor
          // Müşteri ekranı önizlemesini temizle
          (async () => { try { await clearCustomerPreviewForPayment(); } catch(e) {} })();
        }}
        title="Ödeme"
        size="md"
        closeOnOverlayClick={false}
      >
            
            {/* Sipariş Özeti */}
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 mb-6">
              <h4 className="font-semibold text-gray-800 dark:text-white mb-3">
                🍽️ Masa {table?.table_number <= 18 ? `T${table?.table_number}` : `C${table?.table_number - 18}`} - Sipariş Özeti
              </h4>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {cart.map((item, index) => (
                  <div key={index} className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <div className="flex-1">
                        <div className="font-medium text-gray-800 dark:text-white">
                          {item.product?.name}
                          {item.notes === '🎁 ÖDÜL' && (
                            <span className="ml-2 text-xs bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 px-2 py-1 rounded-full">
                              ÖDÜL
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          {item.notes === '🎁 ÖDÜL' ? (
                            <span className="text-green-600 dark:text-green-400 font-medium">
                              {item.quantity} x ÖDÜL
                            </span>
                          ) : item.notes && item.notes !== '🎁 ÖDÜL' ? (
                            <>
                              {item.quantity} x {formatPrice(item.product?.price || 0)}
                              <div className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                                📝 Not: {item.notes}
                              </div>
                            </>
                          ) : (
                            `${item.quantity} x ${formatPrice(item.product?.price || 0)}`
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-medium text-gray-800 dark:text-white">
                          {item.notes === '🎁 ÖDÜL' ? (
                            <span className="text-green-600 dark:text-green-400">
                              ÖDÜL <span className="line-through text-gray-400 ml-2">{formatPrice(item.quantity * (item.product?.price || 0))}</span>
                            </span>
                          ) : (
                            formatPrice(item.quantity * (item.product?.price || 0))
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Toplam ve Vergi Hesaplaması */}
              <div className="border-t border-gray-300 dark:border-gray-600 pt-3 mt-3 space-y-2">
                {(() => {
                  const subtotal = getTotalAmount();
                  const taxTotal = getTaxAmount();
                  let taxInfo = '';
                  
                  if (taxTotal > 0) {
                    // Ürün bazlı vergi oranlarını kontrol et
                    const uniqueTaxRates = new Set(cart.map(item => item.product?.tax_rate || 0).filter(rate => rate > 0));
                    
                    if (uniqueTaxRates.size === 1) {
                      const rate = Array.from(uniqueTaxRates)[0];
                      taxInfo = `KDV (%${rate})`;
                    } else if (uniqueTaxRates.size > 1) {
                      taxInfo = 'KDV (Farklı oranlar)';
                    } else {
                      taxInfo = 'KDV';
                    }
                  } else {
                    taxInfo = 'KDV yok';
                  }
                  
                  return (
                    <>
                      <div className="flex justify-between text-sm text-gray-700 dark:text-gray-300">
                        <span>Ara Toplam:</span>
                        <span>{formatPrice(subtotal)}</span>
                      </div>
                      {taxTotal > 0 && (
                        <div className="flex justify-between text-sm text-gray-700 dark:text-gray-300">
                          <div>
                            <span>Vergi:</span>
                            <div className="text-xs text-gray-500 dark:text-gray-400">{taxInfo}</div>
                          </div>
                          <span>{formatPrice(taxTotal)}</span>
                        </div>
                      )}
                      {giftCodeDiscount > 0 && (
                        <div className="flex justify-between text-sm text-green-600 dark:text-green-400">
                          <div>
                            <span>🎁 Hediye Kodu İndirimi:</span>
                            <div className="text-xs text-gray-500 dark:text-gray-400">{appliedGiftCode?.code}</div>
                          </div>
                          <span>-{formatPrice(giftCodeDiscount)}</span>
                        </div>
                      )}
                      <div className="flex justify-between text-lg font-bold text-gray-800 dark:text-white border-t border-gray-300 dark:border-gray-600 pt-2">
                        <span>TOPLAM:</span>
                        <span className="text-cafe-brown dark:text-cafe-light">{formatPrice(getGrandTotal())}</span>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>

            {/* Hediye Kodu Bölümü */}
            <div className="mb-6">
              <h4 className="font-semibold text-gray-800 dark:text-dark-text mb-3">
                🎁 Hediye Kodu
              </h4>
              
              {!appliedGiftCode ? (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={giftCode}
                    onChange={(e) => setGiftCode(e.target.value.toUpperCase())}
                    placeholder="Hediye kodu girin..."
                    className="flex-1 border border-gray-300 dark:border-dark-border rounded-lg px-3 py-2 bg-white dark:bg-dark-bg text-gray-900 dark:text-dark-text"
                  />
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={validateGiftCode}
                    disabled={!giftCode.trim() || isValidatingCode}
                  >
                    {isValidatingCode ? '⏳' : '✅'} Uygula
                  </Button>
                </div>
              ) : (
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-green-700 dark:text-green-300 font-medium">
                        ✅ {appliedGiftCode.code} uygulandı
                      </span>
                      <div className="text-sm text-green-600 dark:text-green-400">
                        {formatPrice(giftCodeDiscount)} indirim
                      </div>
                    </div>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={removeGiftCode}
                    >
                      ❌ Kaldır
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Sadakat Kodu Bölümü */}
            <div className="mb-6">
              <h4 className="font-semibold text-gray-800 dark:text-dark-text mb-3">
                🎴 Sadakat Kodu
              </h4>
              
              {!appliedLoyaltyCode ? (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={loyaltyCode}
                    onChange={(e) => setLoyaltyCode(e.target.value.toUpperCase())}
                    placeholder="Sadakat kodu girin..."
                    className="flex-1 border border-gray-300 dark:border-dark-border rounded-lg px-3 py-2 bg-white dark:bg-dark-bg text-gray-900 dark:text-dark-text"
                  />
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={validateLoyaltyCode}
                    disabled={!loyaltyCode.trim() || isValidatingLoyaltyCode}
                  >
                    {isValidatingLoyaltyCode ? '⏳' : '✅'} Kontrol Et
                  </Button>
                </div>
              ) : (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-blue-700 dark:text-blue-300 font-medium">
                        🎴 {appliedLoyaltyCode.code}
                      </span>
                      <div className="text-sm text-blue-600 dark:text-blue-400 mt-1">
                        {appliedLoyaltyCode.is_completed ? (
                          appliedLoyaltyCode.reward_used_at ? (
                            '❌ Ödül zaten kullanılmış'
                          ) : (
                            '🎉 Ödül kullanılabilir!'
                          )
                        ) : (
                          `📊 İlerleme: ${appliedLoyaltyCode.current_count + tempLoyaltyCount}/${appliedLoyaltyCode.required_quantity}`
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {((appliedLoyaltyCode.is_completed && !appliedLoyaltyCode.reward_used_at && !pendingLoyaltyReward) || 
                        (tempLoyaltyCount > 0 && (appliedLoyaltyCode.current_count + tempLoyaltyCount) >= appliedLoyaltyCode.required_quantity && !appliedLoyaltyCode.reward_used_at && !pendingLoyaltyReward)) ? (
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={useLoyaltyReward}
                        >
                          🎁 Ödülü Kullan
                        </Button>
                      ) : pendingLoyaltyReward ? (
                        <Button
                          variant="secondary"
                          size="sm"
                          disabled
                        >
                          ✅ Ödül Beklemede
                        </Button>
                      ) : null}
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={removeLoyaltyCode}
                      >
                        ❌ Kaldır
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Ödeme Türü Seçimi */}
            <div className="mb-6">
              <h4 className="font-semibold text-gray-800 dark:text-dark-text mb-3">
                Ödeme Türü Seçin
              </h4>
              <div className="grid grid-cols-3 gap-3">
                <button
                  onClick={() => {
                    setPaymentType('nakit');
                    setWantsReceipt(false);
                  }}
                  className={`p-4 rounded-lg border-2 transition-all duration-200 ${
                    paymentType === 'nakit'
                      ? 'border-green-500 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300'
                      : 'border-gray-300 dark:border-dark-border hover:border-gray-400'
                  }`}
                >
                  <div className="text-2xl mb-2">💵</div>
                  <div className="font-semibold text-gray-800 dark:text-dark-text">Nakit</div>
                </button>
                <button
                  onClick={() => {
                    setPaymentType('kart');
                    setWantsReceipt(false);
                  }}
                  className={`p-4 rounded-lg border-2 transition-all duration-200 ${
                    paymentType === 'kart'
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                      : 'border-gray-300 dark:border-dark-border hover:border-gray-400'
                  }`}
                >
                  <div className="text-2xl mb-2">💳</div>
                  <div className="font-semibold text-gray-800 dark:text-dark-text">Kart</div>
                </button>
                <button
                  onClick={() => {
                    setPaymentType('qr');
                    setWantsReceipt(false);
                  }}
                  className={`p-4 rounded-lg border-2 transition-all duration-200 ${
                    paymentType === 'qr'
                      ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300'
                      : 'border-gray-300 dark:border-dark-border hover:border-gray-400'
                  }`}
                >
                  <div className="text-2xl mb-2">📱</div>
                  <div className="font-semibold text-gray-800 dark:text-dark-text">QR</div>
                </button>
              </div>
            </div>

            {/* Fiş İsteme Onay Kutusu - Sadece Nakit Ödemede */}
            {paymentType === 'nakit' && (
              <div className="mb-6">
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={wantsReceipt}
                    onChange={(e) => setWantsReceipt(e.target.checked)}
                    className="w-4 h-4 text-cafe-brown bg-gray-100 border-gray-300 rounded focus:ring-cafe-brown focus:ring-2"
                  />
                  <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-800'}`}>
                    🧾 Fiş istedi
                  </span>
                </label>
                <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Müşteri fiş istiyorsa bu kutuyu işaretleyin
                </p>
              </div>
            )}

            {/* Butonlar */}
            <div className="flex space-x-3">
              <Button
                variant="secondary"
                size="lg"
                fullWidth
                onClick={async () => { await clearCustomerPreviewForPayment(); setShowPaymentModal(false); }}
              >
                İptal
              </Button>
              {paymentType === 'qr' && (
                <Button
                  variant="primary"
                  size="lg"
                  fullWidth
                  onClick={() => toast.error('QR ödeme masa siparişleri için henüz aktif değil')}
                >
                  📱 QR ile Ödeme
                </Button>
              )}
              {paymentType !== 'qr' && (
                <Button
                  variant="success"
                  size="lg"
                  fullWidth
                  onClick={processPayment}
                  disabled={processing}
                >
                  {processing ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      İşleniyor...
                    </div>
                  ) : (
                    '✅ Ödemeyi Tamamla'
                  )}
                </Button>
              )}
            </div>
          </Modal>

             {/* Fiş Modalı */}
       <Modal
         isOpen={showReceiptModal}
         onClose={() => setShowReceiptModal(false)}
         title="Fiş"
         size="md"
       >
        <div className="text-center space-y-4">
          <div className="text-green-600">
            <div className="text-4xl mb-3">✅</div>
                         <h3 className="text-xl font-bold mb-2">Ödeme Başarıyla Alındı!</h3>
                         <p className="text-gray-600 dark:text-dark-text">
               Sipariş #{completedOrder?.id || order?.id} - Masa {table?.table_number}<br />
               Toplam: {formatPrice(completedOrder?.total_amount || order?.total_amount || 0)}<br />
               Ödeme Türü: {(completedOrder?.payment_type || paymentType)?.toUpperCase()}
             </p>
          </div>
          
          <div className="flex space-x-3">
            <Button
              variant="primary"
              size="lg"
              fullWidth
              onClick={printReceipt}
            >
              🖨️ Fiş Yazdır
            </Button>
            <Button
              variant="success"
              size="lg"
              fullWidth
              onClick={finishOrder}
            >
              ✅ Tamamla
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default TableOrder;












